import React, { useState, useEffect, useContext } from "react";
import {
  createExpansive,
  updateExpansive,
  getCategory,
  getExpansiveById,
} from "../../api/Api";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Setting from "../settings/Setting";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../../utils/AuthContext";

const cashOptions = ["Cash", "Cheque", "QR", "Bank Transfer"];

const AddExpansive = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { selectedBranchId, academicYear } = useContext(AuthContext);

  // Form States
  const [payeeName, setPayeeName] = useState("");
  const [payeePhoneNumber, setPayeePhoneNumber] = useState("");
  const [particulars, setParticulars] = useState("");
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [amountInWords, setAmountInWords] = useState("");
  const [approved, setApproved] = useState(false);
  const [accountentName, setAccountentName] = useState("");
  const [billNo, setBillNo] = useState("");

  // Payment Mode States
  const [cashMode, setCashMode] = useState("");
  const [cashDenominations, setCashDenominations] = useState("");
  const [qrTransactionId, setQrTransactionId] = useState("");
  const [bankTransferId, setBankTransferId] = useState("");
  const [chequeDetails, setChequeDetails] = useState("");

  // Category State
  const [categories, setCategories] = useState([]);

  // Fetch Data
  useEffect(() => {
    fetchCategories();

    if (id) {
      fetchExpansive();
    }
  }, [id]);

  // Fetch Categories
  const fetchCategories = async () => {
    try {
      const response = await getCategory();

      if (response?.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Fetch Expansive Data
  const fetchExpansive = async () => {
    try {
      const response = await getExpansiveById(id);

      if (response.success) {
        const expansive = response.data;

        setPayeeName(expansive.payeeName || "");
        setPayeePhoneNumber(expansive.payeePhoneNumber || "");
        setParticulars(expansive.particulars || "");
        setCategory(expansive.category || "");
        setAmount(expansive.amount || "");
        setAmountInWords(expansive.amountInWords || "");
        setApproved(expansive.approved || false);
        setAccountentName(expansive.AccountentName || "");
        setCashMode(expansive.paymentMode || "");
        setCashDenominations(expansive.cashDenominations || "");
        setQrTransactionId(expansive.qrTransactionId || "");
        setBankTransferId(expansive.bankTransferId || "");
        setChequeDetails(expansive.chequeDetails || "");
        setBillNo(expansive.billNo || "");
      }
    } catch (error) {
      console.error("Error fetching expansive:", error);
    }
  };

  // Convert Number to Words
  const numberToWords = (num) => {
    const ones = [
      "",
      "one",
      "two",
      "three",
      "four",
      "five",
      "six",
      "seven",
      "eight",
      "nine",
    ];

    const teens = [
      "ten",
      "eleven",
      "twelve",
      "thirteen",
      "fourteen",
      "fifteen",
      "sixteen",
      "seventeen",
      "eighteen",
      "nineteen",
    ];

    const tens = [
      "",
      "",
      "twenty",
      "thirty",
      "forty",
      "fifty",
      "sixty",
      "seventy",
      "eighty",
      "ninety",
    ];

    const getTwoDigits = (n) => {
      if (n < 10) return ones[n];

      if (n < 20) return teens[n - 10];

      return (
        tens[Math.floor(n / 10)] +
        (n % 10 !== 0 ? " " + ones[n % 10] : "")
      );
    };

    const getThreeDigits = (n) => {
      if (n === 0) return "";

      const hundred = Math.floor(n / 100);
      const rest = n % 100;

      return (
        (hundred
          ? ones[hundred] + " hundred" + (rest ? " and " : "")
          : "") + getTwoDigits(rest)
      );
    };

    if (num === 0) return "zero";

    let result = "";

    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = Math.floor(num % 1000);

    if (crore) result += getTwoDigits(crore) + " crore ";
    if (lakh) result += getTwoDigits(lakh) + " lakh ";
    if (thousand) result += getTwoDigits(thousand) + " thousand ";
    if (hundred) result += getThreeDigits(hundred);

    return result.trim();
  };

  // Handle Amount Change
  const handleAmountChange = (e) => {
    const value = e.target.value;

    setAmount(value);
    setAmountInWords(numberToWords(Number(value)));
  };

  // Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const expansiveDetails = {
      payeeName,
      payeePhoneNumber,
      particulars,
      category,
      amount,
      amountInWords,
      AccountentName: accountentName,
      approved,
      paymentMode: cashMode,
      cashDenominations,
      qrTransactionId,
      bankTransferId,
      chequeDetails,
      billNo,
      branch:
        selectedBranchId || localStorage.getItem("selectedBranchId"),
      academicYear,
    };

    console.log("Submitting Data:", expansiveDetails);

    try {
      const response = id
        ? await updateExpansive(id, expansiveDetails)
        : await createExpansive(expansiveDetails);

      if (response.success) {
        toast.success(
          id
            ? "Expansive updated successfully!"
            : "Expansive added successfully!"
        );

        setTimeout(() => {
          navigate("/admin/accounting");
        }, 1000);
      } else {
        toast.error(response.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Submit Error:", error);
      toast.error("Failed to submit expansive data");
    }
  };

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Main Form */}
      <div className="p-6 max-w-4xl mx-auto bg-white shadow-md rounded-lg">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate("/admin/accounting")}
            className="text-black mr-4"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>

          <h2 className="text-2xl font-bold text-[#16bab1]">
            {id ? "✏️ Edit Expansive" : "💵 Add Expansive"}
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-[2fr_1fr] gap-4 mb-6">
            {/* Left Section */}
            <div>
              {/* Payee Name */}
              <div className="flex items-center space-x-1 mb-4">
                <label className="bg-[#16bab1] text-white w-44 px-2 py-1">
                  Payee Name:
                </label>

                <input
                  type="text"
                  value={payeeName}
                  onChange={(e) => setPayeeName(e.target.value)}
                  placeholder="Enter Payee Name"
                  className="bg-gray-200 outline-none px-3 py-1 flex-1"
                  required
                />
              </div>

              {/* Phone Number */}
              <div className="flex items-center space-x-1 mb-4">
                <label className="bg-[#16bab1] text-white w-44 px-2 py-1">
                  Phone Number:
                </label>

                <input
                  type="tel"
                  value={payeePhoneNumber}
                  onChange={(e) => setPayeePhoneNumber(e.target.value)}
                  placeholder="Enter Phone Number"
                  className="bg-gray-200 outline-none px-3 py-1 flex-1"
                  required
                />
              </div>

              {/* Particulars */}
              <div className="flex items-center space-x-1 mb-4">
                <label className="bg-[#16bab1] text-white w-44 px-2 py-1">
                  Particulars:
                </label>

                <input
                  type="text"
                  value={particulars}
                  onChange={(e) => setParticulars(e.target.value)}
                  placeholder="Enter Details"
                  className="bg-gray-200 outline-none px-3 py-1 flex-1"
                  required
                />
              </div>

              {/* Accountant Name */}
              <div className="flex items-center space-x-1 mb-4">
                <label className="bg-[#16bab1] text-white w-44 px-2 py-1">
                  Accountant Name:
                </label>

                <input
                  type="text"
                  value={accountentName}
                  onChange={(e) => setAccountentName(e.target.value)}
                  placeholder="Enter Accountant Name"
                  className="bg-gray-200 outline-none px-3 py-1 flex-1"
                  required
                />
              </div>

              {/* Bill No */}
              <div className="flex items-center space-x-1 mb-4">
                <label className="bg-[#16bab1] text-white w-44 px-2 py-1">
                  Bill No:
                </label>

                <input
                  type="text"
                  value={billNo}
                  onChange={(e) => setBillNo(e.target.value)}
                  placeholder="Enter Bill Number"
                  className="bg-gray-200 outline-none px-3 py-1 flex-1"
                  required
                />
              </div>
            </div>

            {/* Right Section */}
            <div>
              {/* Category */}
              <div className="mb-4 flex flex-col">
                <label className="bg-[#16bab1] text-white px-2 py-1">
                  Category
                </label>

                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="bg-gray-200 outline-none px-2 py-1"
                  required
                >
                  <option value="">Select Category</option>

                  {categories.map((cat) => (
                    <option key={cat._id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div className="mb-4 flex flex-col">
                <label className="bg-[#16bab1] text-white px-2 py-1">
                  Amount
                </label>

                <input
                  type="number"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="Enter Amount"
                  className="bg-gray-200 outline-none px-2 py-1"
                  required
                />
              </div>
            </div>
          </div>

          {/* Amount In Words */}
          <div className="flex items-center space-x-1 mb-6">
            <label className="bg-[#16bab1] text-white w-36 px-2 py-1">
              Rs. (in words):
            </label>

            <input
              type="text"
              value={amountInWords}
              onChange={(e) => setAmountInWords(e.target.value)}
              placeholder="Amount in Words"
              className="bg-gray-200 outline-none w-full px-4 py-1"
              required
            />
          </div>

          {/* Payment Mode */}
          <div className="mb-4">
            <label className="bg-[#16bab1] text-white w-full px-2 py-1 inline-block">
              Mode of Payment:
            </label>

            <div className="grid grid-cols-4 gap-2 mt-2">
              {cashOptions.map((option) => (
                <div
                  key={option}
                  onClick={() => {
                    setCashMode(option);

                    setChequeDetails("");
                    setQrTransactionId("");
                    setBankTransferId("");
                  }}
                  className={`cursor-pointer px-2 py-2 border rounded text-center ${
                    cashMode === option
                      ? "bg-[#16bab1] text-white border-[#226a69]"
                      : "border-gray-400 text-gray-700"
                  }`}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>

          {/* Cash */}
          {cashMode === "Cash" && (
            <div className="mb-4 flex flex-col">
              <label className="bg-[#16bab1] text-white px-2 py-1">
                Cash Denomination
              </label>

              <textarea
                className="bg-gray-200 outline-none px-2 py-1"
                rows={2}
                placeholder="500 x 2, 100 x 5"
                value={cashDenominations}
                onChange={(e) => setCashDenominations(e.target.value)}
              />
            </div>
          )}

          {/* QR */}
          {cashMode === "QR" && (
            <div className="mb-4 flex flex-col">
              <label className="bg-[#16bab1] text-white px-2 py-1">
                QR Transaction ID
              </label>

              <input
                type="text"
                className="bg-gray-200 outline-none px-2 py-1"
                placeholder="Enter Transaction ID"
                value={qrTransactionId}
                onChange={(e) => setQrTransactionId(e.target.value)}
              />
            </div>
          )}

          {/* Bank Transfer */}
          {cashMode === "Bank Transfer" && (
            <div className="mb-4 flex flex-col">
              <label className="bg-[#16bab1] text-white px-2 py-1">
                Bank Transfer ID
              </label>

              <input
                type="text"
                className="bg-gray-200 outline-none px-2 py-1"
                placeholder="Enter Transfer ID"
                value={bankTransferId}
                onChange={(e) => setBankTransferId(e.target.value)}
              />
            </div>
          )}

          {/* Cheque */}
          {cashMode === "Cheque" && (
            <div className="mb-4 flex flex-col">
              <label className="bg-[#16bab1] text-white px-2 py-1">
                Cheque Details
              </label>

              <input
                type="text"
                className="bg-gray-200 outline-none px-2 py-1"
                placeholder="Enter Cheque Details"
                value={chequeDetails}
                onChange={(e) => setChequeDetails(e.target.value)}
              />
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="submit"
              className="w-[24%] bg-[#16bab1] hover:bg-[#139e97] text-white font-semibold rounded-lg p-3 transition duration-200"
            >
              {id ? "Update" : "Submit"}
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-[24%] bg-[#F36743] hover:bg-[#dd5430] text-white font-semibold rounded-lg p-3 transition duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Setting Section */}
      <div className="p-6 mt-6 max-w-4xl mx-auto bg-white shadow-md rounded-lg">
        <Setting />
      </div>
    </div>
  );
};

export default AddExpansive;